// Init i18next pour Revyo.
// IMPORTANT : initI18n() est appelé dans frontend/src/index.js et DOIT être
// résolu AVANT ReactDOM.render — sinon les composants montés se rendent avec
// la mauvaise langue, puis re-render après detect, ce qui fait clignoter le UI.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LANGS, DEFAULT_LANG } from './config';
import { detectInitialLanguage } from './detectLang';

// Bundles statiques chargés via import (CRA fait le tree-shaking sur les JSON).
// P1 : common, footer | P2 : funnel | P3 : landing, pricing, howItWorks, blog, contact, seo
// Si le bundle devient trop gros (>30 KB gzip vs avant P3), basculer sur
// i18next-http-backend pour du lazy par namespace. Aujourd'hui : imports statiques.
import frCommon from './locales/fr/common.json';
import frFooter from './locales/fr/footer.json';
import frFunnel from './locales/fr/funnel.json';
import frLanding from './locales/fr/landing.json';
import frPricing from './locales/fr/pricing.json';
import frHowItWorks from './locales/fr/howItWorks.json';
import frBlog from './locales/fr/blog.json';
import frContact from './locales/fr/contact.json';
import frSeo from './locales/fr/seo.json';
import frAuth from './locales/fr/auth.json';
import frLegal from './locales/fr/legal.json';
import frDashboard from './locales/fr/dashboard.json';
import frTabs from './locales/fr/tabs.json';
import frErrors from './locales/fr/errors.json';
import frScoreRps from './locales/fr/scoreRps.json';

import enCommon from './locales/en/common.json';
import enFooter from './locales/en/footer.json';
import enFunnel from './locales/en/funnel.json';
import enLanding from './locales/en/landing.json';
import enPricing from './locales/en/pricing.json';
import enHowItWorks from './locales/en/howItWorks.json';
import enBlog from './locales/en/blog.json';
import enContact from './locales/en/contact.json';
import enSeo from './locales/en/seo.json';
import enAuth from './locales/en/auth.json';
import enLegal from './locales/en/legal.json';
import enDashboard from './locales/en/dashboard.json';
import enTabs from './locales/en/tabs.json';
import enErrors from './locales/en/errors.json';
import enScoreRps from './locales/en/scoreRps.json';

import esCommon from './locales/es/common.json';
import esFooter from './locales/es/footer.json';
import esFunnel from './locales/es/funnel.json';
import esLanding from './locales/es/landing.json';
import esPricing from './locales/es/pricing.json';
import esHowItWorks from './locales/es/howItWorks.json';
import esBlog from './locales/es/blog.json';
import esContact from './locales/es/contact.json';
import esSeo from './locales/es/seo.json';
import esAuth from './locales/es/auth.json';
import esLegal from './locales/es/legal.json';
import esDashboard from './locales/es/dashboard.json';
import esTabs from './locales/es/tabs.json';
import esErrors from './locales/es/errors.json';
import esScoreRps from './locales/es/scoreRps.json';

import itCommon from './locales/it/common.json';
import itFooter from './locales/it/footer.json';
import itFunnel from './locales/it/funnel.json';
import itLanding from './locales/it/landing.json';
import itPricing from './locales/it/pricing.json';
import itHowItWorks from './locales/it/howItWorks.json';
import itBlog from './locales/it/blog.json';
import itContact from './locales/it/contact.json';
import itSeo from './locales/it/seo.json';
import itAuth from './locales/it/auth.json';
import itLegal from './locales/it/legal.json';
import itDashboard from './locales/it/dashboard.json';
import itTabs from './locales/it/tabs.json';
import itErrors from './locales/it/errors.json';
import itScoreRps from './locales/it/scoreRps.json';

const NAMESPACES = ['common', 'footer', 'funnel', 'landing', 'pricing', 'howItWorks', 'blog', 'contact', 'seo', 'auth', 'legal', 'dashboard', 'tabs', 'errors', 'scoreRps'];

// Synchronise <html lang="..."> avec la langue active.
// Critique SEO : Google s'appuie sur cet attribut pour servir la bonne
// variante par hreflang. Sans ce sync, on reste sur le lang="en" de
// public/index.html quelle que soit la langue runtime.
function syncHtmlLang(lang) {
  if (typeof document === 'undefined' || !document.documentElement) return;
  if (SUPPORTED_LANGS.includes(lang)) {
    document.documentElement.lang = lang;
  }
}

export async function initI18n() {
  const initialLang = await detectInitialLanguage();

  await i18n
    .use(initReactI18next)
    .init({
      lng: initialLang,
      fallbackLng: DEFAULT_LANG,
      supportedLngs: SUPPORTED_LANGS,
      ns: NAMESPACES,
      defaultNS: 'common',
      resources: {
        fr: {
          common: frCommon, footer: frFooter, funnel: frFunnel,
          landing: frLanding, pricing: frPricing, howItWorks: frHowItWorks,
          blog: frBlog, contact: frContact, seo: frSeo,
          auth: frAuth, legal: frLegal,
          dashboard: frDashboard, tabs: frTabs,
          errors: frErrors, scoreRps: frScoreRps,
        },
        en: {
          common: enCommon, footer: enFooter, funnel: enFunnel,
          landing: enLanding, pricing: enPricing, howItWorks: enHowItWorks,
          blog: enBlog, contact: enContact, seo: enSeo,
          auth: enAuth, legal: enLegal,
          dashboard: enDashboard, tabs: enTabs,
          errors: enErrors, scoreRps: enScoreRps,
        },
        es: {
          common: esCommon, footer: esFooter, funnel: esFunnel,
          landing: esLanding, pricing: esPricing, howItWorks: esHowItWorks,
          blog: esBlog, contact: esContact, seo: esSeo,
          auth: esAuth, legal: esLegal,
          dashboard: esDashboard, tabs: esTabs,
          errors: esErrors, scoreRps: esScoreRps,
        },
        it: {
          common: itCommon, footer: itFooter, funnel: itFunnel,
          landing: itLanding, pricing: itPricing, howItWorks: itHowItWorks,
          blog: itBlog, contact: itContact, seo: itSeo,
          auth: itAuth, legal: itLegal,
          dashboard: itDashboard, tabs: itTabs,
          errors: itErrors, scoreRps: itScoreRps,
        },
      },
      interpolation: {
        // React échappe déjà — désactive le double-escape qui casse les accents.
        escapeValue: false,
      },
      react: {
        // useSuspense=false → on contrôle nous-mêmes le timing (initI18n await
        // AVANT render), pas besoin du fallback Suspense.
        useSuspense: false,
      },
      // Mode dev uniquement : log des clés manquantes en console (côté navigateur).
      // En prod (Vercel build), process.env.NODE_ENV='production' → silencieux.
      debug: false,
    });

  // Listener pour synchroniser <html lang> en runtime + premier sync immédiat.
  syncHtmlLang(initialLang);
  i18n.on('languageChanged', (lang) => syncHtmlLang(lang));

  return i18n;
}

export default i18n;
